import React, { useState, useRef, useEffect } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { FAQPageJsonLd } from 'next-seo'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Accordion, Option, WContainer } from '@next-nx/shared-ui/atoms'
import { FaqSearchAndMacroCategories } from '@next-nx/shared-ui/molecules'
import {
  CategorieFaqDetailInterface,
  CategoriaFaqInterface,
  FaqInterface,
} from '@next-nx/utils/types'
import { faqMacroCategories } from 'libs/shared-ui/utils/const'
import { useTealium } from '@next-nx/hooks';
import { getTimestamp } from '@next-nx/utils';
import styles from './FaqListingSection.module.sass'

interface Props {
  categorie: CategorieFaqDetailInterface[]
  categoriaMain: CategoriaFaqInterface
  selectedFaq: string
  className?: string
}

const getAllFaqs = (
  categorie: CategorieFaqDetailInterface[],
  categorySlug: string = ''
): FaqInterface[] => {
  let f: FaqInterface[] = []

  if (categorySlug)
    categorie.forEach((item) => {
      if (categorySlug === item.slug) f = item.faq
    })
  else categorie.forEach((item) => (f = f.concat(item.faq)))

  return f
}

const getInitialSlide = (categorie: CategorieFaqDetailInterface[], categorySlug: string) => {
  let i = 0
  categorie.forEach((value, index) => {
    if (value.slug === categorySlug) i = index
  })
  return i
}

const FaqListingSection = (props: Props) => {
  const { categorie, categoriaMain, selectedFaq, className = '' } = props

  const router = useRouter()
  const { linkDataLayer } = useTealium()

  const [selectedMacroCategory, setSelectedMacroCategory] = useState<string>(
    categoriaMain.macroCategoria ? categoriaMain.macroCategoria : faqMacroCategories[0].key
  )

  const allFaqs = useRef<FaqInterface[]>(getAllFaqs(categorie))
  const mainFaqs: FaqInterface[] = getAllFaqs(categorie, categoriaMain.slug)
  const categories = categorie.filter(value => { return value.macroCategoria === selectedMacroCategory && value.titolo })
  const mobileInitialSlide = getInitialSlide(categories, categoriaMain.slug)

  const handleTracking = (faq_title: string = '') => {
    const action_timestamp = getTimestamp() 
    
    linkDataLayer({
      virtual: 'action', 
      action_name: 'faq',  
      action_tech: 'click', 
      action_type: 'consulta_faq',
      action_timestamp,
      action_effect: 'faq_interazione',
      faq_category: categoriaMain?.titolo?.replace(/\-|\s+/g, '_').toLowerCase(),
      faq_title: faq_title.replace(/\-|\s+/g, '_').toLowerCase(),
    }) 
  } 

  useEffect(() => {
    setSelectedMacroCategory(
      categoriaMain.macroCategoria ? categoriaMain.macroCategoria : faqMacroCategories[0].key
    )
  }, [categoriaMain])

  return (
    <div className={`${styles.root} ${className}`}>
      <FaqSearchAndMacroCategories
        selectedMacroCategory={selectedMacroCategory}
        macroCategoryOnClick={(macroCategoriaKey: string) => {
          const url = categorie.filter(value => { return value.macroCategoria === macroCategoriaKey })[0].url
          router.push({ pathname: url })
        }}
        faqs={allFaqs.current}
      />
      <WContainer>
        <div className={styles.faq_box}>
          <div className={styles.faq_box_categories}>
            <div className={styles.faq_box_categories_background}>
              {categories.map((item, index) => (
                <Link href={item.url} key={index + item.slug} passHref>
                  <a>
                    <button
                      className={`${styles.faq_box_categories_item} ${
                        categoriaMain.slug === item.slug
                          ? styles.faq_box_categories_item_selected
                          : ''
                      }`}
                    >
                      <li>{item.titolo}</li>
                    </button>
                  </a>
                </Link>
              ))}
            </div>
            <div className={styles.faq_box_categories_mobile}>
              <Swiper
                spaceBetween={16}
                slidesPerView="auto"
                initialSlide={mobileInitialSlide}
                watchOverflow
              >
                {categories.map((item, index) => (
                  <SwiperSlide key={item.slug} className={styles.faq_box_categories_mobile_item}>
                    <Link href={item.url} passHref>
                      <a>
                        <Option
                          label={item.titolo ? item.titolo : ''}
                          active={categoriaMain.slug === item.slug}
                        />
                      </a>
                    </Link>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
          <div className={styles.faq_box_listing}>
            <h1 className={styles.faq_box_title}>FAQ - {categoriaMain.titolo}</h1>
            {mainFaqs.map((item, index) => (
              <Accordion
                key={index + '_' + item.url}
                className={styles.faq_box_listing_item}
                label={item.domanda ? item.domanda : ''}
                content={item.risposta ? item.risposta : ''}
                isOpen={selectedFaq !== '' ? selectedFaq === item.slug : index === 0}
                handleTracking={handleTracking} />
            ))}
          </div>
        </div>
      </WContainer>
      <FAQPageJsonLd
        mainEntity={mainFaqs.map((item) => {
          return {
            questionName: item.domanda ? item.domanda : '',
            acceptedAnswerText: item.risposta
              ? item.risposta.replace(/<\/?[^>]+(>|$)/gi, '').replace(/\&nbsp;/g, ' ')
              : '',
          }
        })}
      />
    </div>
  )
}

export default FaqListingSection
